
<v-card
  class="integration-card"
  rounded
  outlined
  flat
  role="article"
  tabindex="0"
  @click="handleServiceEdit"
  @keydown.enter="handleServiceEdit"
  @keydown.space.prevent="handleServiceEdit"
  :aria-label="`Integration: ${getServiceName()}, Status: ${getStatusText()}, Last run: ${getLastRunAriaLabel()}`"
  :data-testid="`integration-card-${integrationService.id}`"
>
  <div class="landscape-layout" :class="`landscape-layout--${getStatusIconColor()}`">
    <!-- Left Section: Sparkline with Stats -->
<div v-if="hasSparklineData" class="sparkline-section" role="img" :aria-label="getSparklineAriaLabel()">
      <v-sheet
        class="sparkline-container"
        :class="`sparkline-container--${getStatusIconColor()}`"
        :data-testid="`sparkline-${integrationService.id}`"
      >
        <div class="sparkline-chart-area">
  <v-tooltip top>
            <template #activator="{ on, attrs }">
              <v-sparkline
                v-bind="attrs"
                v-on="on"
                :value="sparklineValues"
                color="#666666"
                :height="30"
                :width="160"
                type="trend"
                :auto-draw="false"
                :line-width="1"
                :padding="4"
                :smooth="false"
                class="grey-sparkline"
                :aria-hidden="true"
              ></v-sparkline>
            </template>
            <span>{{ getSparklineTooltip() }}</span>
          </v-tooltip>
        </div>

        <!-- Stats Row -->
        <div
          class="sparkline-stats"
          role="group"
          :aria-label="'Performance statistics for ' + getServiceName()"
          :data-testid="`stats-${integrationService.id}`"
        >
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <div class="sparkline-stat-item" v-bind="attrs" v-on="on">
                <div class="sparkline-stat-value" :class="successCount > 0 ? 'success--text' : 'grey--text'">{{ successCount }}</div>
                <div class="sparkline-stat-label">Success</div>
              </div>
            </template>
            <span>Successful operations in the last 24 hours</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <div class="sparkline-stat-item" v-bind="attrs" v-on="on">
                <div class="sparkline-stat-value" :class="failureCount > 0 ? 'error--text' : 'grey--text'">{{ failureCount }}</div>
                <div class="sparkline-stat-label">Failures</div>
              </div>
            </template>
            <span>Failed operations in the last 24 hours</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <div class="sparkline-stat-item" v-bind="attrs" v-on="on">
                <div class="sparkline-stat-value" :class="localEnabled ? 'primary--text' : 'grey--text'">{{ successRate }}</div>
                <div class="sparkline-stat-label">Rate</div>
              </div>
            </template>
            <span>Success rate percentage over the last 24 hours</span>
          </v-tooltip>
        </div>
      </v-sheet>
    </div>

    <!-- Middle Section: Content -->
<div class="content-section" :class="contentSectionClasses">
      <!-- Accessibility note when no sparkline data is present -->
      <div v-if="!hasSparklineData" class="sr-only" aria-live="polite">
        No activity data available for this service.
      </div>
      <!-- Header with title -->
      <div class="content-header">
        <div class="title-section">
          <div class="service-name-row">
            <div class="service-title-container">
              <div class="service-name-container">
                <div class="service-name" :data-testid="`integration-name-${integrationService.id}`">
                  {{ getServiceName() }}
                </div>
                <!-- Status indicator: Last Run or Disabled -->
                <div class="last-run-indicator">
                  <v-icon
                    class="last-run-icon"
                    small
                    :aria-hidden="true"
                  >
                    {{ localEnabled ? 'mdi-check-circle-outline' : 'mdi-pause-circle-outline' }}
                  </v-icon>
                  <span
                    class="last-run-text"
                    :data-testid="`last-run-${integrationService.id}`"
                    :aria-label="localEnabled ? 'Last execution: ' + getLastRunText() : 'Integration disabled'"
                  >
                    {{ localEnabled ? getLastRunText() : 'Disabled' }}
                  </span>
                </div>
              </div>
              <div v-if="integrationService.notes" class="service-description" :data-testid="`integration-description-${integrationService.id}`">
                {{ getTruncatedNotes() }}
              </div>
            </div>
          </div>
        </div>
        <!-- Actions Menu -->
        <div class="content-actions">
          <v-menu bottom left>
            <template #activator="{ on, attrs }">
              <v-btn icon x-small v-bind="attrs" v-on="on" :aria-label="'Actions menu for ' + getServiceName()" :data-testid="`integration-menu-${integrationService.id}`">
                <v-icon>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>
            <v-list dense role="menu" :aria-label="'Actions for ' + getServiceName()">
              <v-list-item
                v-if="userHasFullConnectorServicePermission && integrationService.isEditable"
                @click="handleServiceEdit"
                role="menuitem"
                :data-testid="`edit-service-${integrationService.id}`"
              >
                <v-list-item-title>Edit</v-list-item-title>
              </v-list-item>
              <v-list-item
                v-else
                @click="handleServiceEdit"
                role="menuitem"
                :data-testid="`view-service-${integrationService.id}`"
              >
                <v-list-item-title>View</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="userHasFullConnectorServicePermission" @click="handleServiceExport" role="menuitem" :data-testid="`export-service-${integrationService.id}`">
                <v-list-item-title>Export</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="userHasFullConnectorServicePermission" @click="handleServiceClone" role="menuitem" :data-testid="`clone-service-${integrationService.id}`">
                <v-list-item-title>Clone</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="integrationService.failures?.length > 0 && userHasLogViewPermission" @click="handleViewFailures" role="menuitem" :data-testid="`view-failures-${integrationService.id}`">
                <v-list-item-title>View Failure Logs</v-list-item-title>
              </v-list-item>
              <v-list-item
                v-if="userHasFullConnectorServicePermission"
                :disabled="!integrationService.isEditable"
                @click.capture.stop.prevent="integrationService.isEditable ? handleDeleteService() : null"
                role="menuitem"
                :data-testid="`delete-service-${integrationService.id}`"
              >
                <v-list-item-title>Delete</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
  <!-- HTTP Method and URL with Switch -->
<div class="url-section">
        <div v-if="showEndpoint" class="url-group" role="group" :aria-label="'HTTP endpoint: ' + getHttpMethod() + ' ' + integrationService.requestUrl" :data-testid="`endpoint-${integrationService.id}`">
          <v-tooltip top>
            <template #activator="{ on, attrs }">
              <div
                class="http-method-chip http-method-primary"
                v-bind="attrs"
                v-on="on"
                :aria-label="`HTTP method: ${getHttpMethod()}`"
              >
                {{ getHttpMethod() }}
              </div>
            </template>
            <span>HTTP method for this service endpoint</span>
          </v-tooltip>
          <div class="url-field">
            <span class="url-text" :title="integrationService.requestUrl" :aria-label="'Endpoint URL: ' + integrationService.requestUrl">
              {{ integrationService.requestUrl }}
            </span>
          </div>
        </div>
        <div v-else class="s3-indicator-wrapper">
          <v-tooltip top>
            <template #activator="{ on, attrs }">
              <div class="s3-indicator" v-bind="attrs" v-on="on" role="note" :aria-label="'Direct to S3 mode'" :data-testid="`endpoint-s3-${integrationService.id}`">
                <v-icon color="primary" size="28" aria-hidden="true">mdi-cloud-upload-outline</v-icon>
                <div class="text-subtitle-2 ml-1">Direct to S3</div>
              </div>
            </template>
            <span>Direct to S3 mode doesn't use an HTTP endpoint</span>
          </v-tooltip>
        </div>

        <!-- Switch -->
        <div class="url-switch-container" @click.stop>
          <v-tooltip top>
            <template #activator="{ on, attrs }">
              <v-switch
                v-model="localEnabled"
                :disabled="!integrationService.isEditable || !userHasFullConnectorServicePermission"
                color="primary"
                inset
                dense
                class="integration-switch cassie-custom-toggle"
                :class="{ 'toggle-on': localEnabled, 'toggle-off': !localEnabled }"
                v-bind="attrs"
                v-on="on"
                @change="handleToggleEnabled"
                :aria-label="`${localEnabled ? 'Disable' : 'Enable'} service: ${getServiceName()}`"
                :data-testid="`integration-toggle-${integrationService.id}`"
                role="switch"
                :aria-checked="localEnabled"
              ></v-switch>
            </template>
            <span>{{ localEnabled ? 'Disable' : 'Enable' }} this service</span>
          </v-tooltip>
        </div>
      </div>
    </div>
  </div>
</v-card>
